import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './SensorManagement.css';
import DataTable from './DataTable';
import Modal from './Modal';
import { API_BASE_URL } from './constants/config';

const SensorManagement = ({ selectedCompany, onClose, onNavigateToLogs, theme = 'light', autoRefreshEnabled = false }) => {
  const [sensors, setSensors] = useState([]);
  const [washers, setWashers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingSensor, setEditingSensor] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [connectionAttempt, setConnectionAttempt] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState('');
  const [sensorForm, setSensorForm] = useState({
    mac_address: '',
    sensor_name: '',
    status: 'active',
    notes: ''
  });

  // センサー列設定（デフォルト定義）— 未割り当て/割り当て済み共通
  const defaultSensorColumns = [
    { id: 'mac_address', label: 'MACアドレス', sortable: true, visible: true, fixed: false, width: 180 },
    { id: 'sensor_name', label: 'センサー名', sortable: true, visible: true, fixed: false, width: 150 },
    { id: 'assigned_washer', label: '割り当て洗浄機', sortable: true, visible: true, fixed: false, width: 200 },
    { id: 'status', label: 'ステータス', sortable: true, visible: true, fixed: false, width: 100 },
    { id: 'notes', label: '備考', sortable: true, visible: true, fixed: false, width: 200 },
    { id: 'last_boot_at', label: '最終起動日時', sortable: true, visible: true, fixed: false, width: 180 },
    { id: 'last_response_at', label: '最終応答日時', sortable: true, visible: true, fixed: false, width: 180 },
    { id: 'actions', label: '操作', sortable: false, visible: true, fixed: true, width: 280 }
  ];

  // localStorageから列設定を読み込む
  const loadSensorColumns = () => {
    try {
      const saved = localStorage.getItem('sensorColumns');
      if (saved) {
        const parsed = JSON.parse(saved);
        // デフォルト列定義と統合（新しい列が追加された場合に対応）
        const merged = [...defaultSensorColumns];
        parsed.forEach(savedCol => {
          const index = merged.findIndex(col => col.id === savedCol.id);
          if (index >= 0) {
            const base = merged[index];
            merged[index] = {
              ...base,
              visible: savedCol.visible ?? base.visible,
              sortable: savedCol.sortable ?? base.sortable,
              width: savedCol.width ?? base.width,
              fixed: savedCol.fixed ?? base.fixed
            };
          }
        });
        return merged;
      }
    } catch (e) {
      console.error('Failed to load sensor columns from localStorage:', e);
    }
    return defaultSensorColumns;
  };

  // センサー列設定のstate
  const [sensorColumns, setSensorColumns] = useState(loadSensorColumns());
  
  // 未割り当てセンサー用ページネーション・ソート
  const [unassignedPage, setUnassignedPage] = useState(() => {
    const saved = localStorage.getItem('unassignedSensorPage');
    return saved ? parseInt(saved, 10) : 1;
  });
  const [unassignedPageSize, setUnassignedPageSize] = useState(() => {
    const saved = localStorage.getItem('unassignedSensorPageSize');
    return saved ? parseInt(saved, 10) : 20;
  });
  const [unassignedSort, setUnassignedSort] = useState(() => {
    const saved = localStorage.getItem('unassignedSensorSort');
    return saved ? JSON.parse(saved) : { column: 'mac_address', order: 'asc' };
  });
  
  // 割り当て済みセンサー用ページネーション・ソート
  const [assignedPage, setAssignedPage] = useState(() => {
    const saved = localStorage.getItem('assignedSensorPage');
    return saved ? parseInt(saved, 10) : 1;
  });
  const [assignedPageSize, setAssignedPageSize] = useState(() => {
    const saved = localStorage.getItem('assignedSensorPageSize');
    return saved ? parseInt(saved, 10) : 20;
  });
  const [assignedSort, setAssignedSort] = useState(() => {
    const saved = localStorage.getItem('assignedSensorSort');
    return saved ? JSON.parse(saved) : { column: 'mac_address', order: 'asc' };
  });
  
  // ページング・ソート設定をLocalStorageに保存
  React.useEffect(() => {
    localStorage.setItem('unassignedSensorPage', unassignedPage.toString());
  }, [unassignedPage]);
  
  React.useEffect(() => {
    localStorage.setItem('unassignedSensorPageSize', unassignedPageSize.toString());
  }, [unassignedPageSize]);
  
  React.useEffect(() => {
    localStorage.setItem('unassignedSensorSort', JSON.stringify(unassignedSort));
  }, [unassignedSort]);
  
  React.useEffect(() => {
    localStorage.setItem('assignedSensorPage', assignedPage.toString());
  }, [assignedPage]);
  
  React.useEffect(() => {
    localStorage.setItem('assignedSensorPageSize', assignedPageSize.toString());
  }, [assignedPageSize]);
  
  React.useEffect(() => {
    localStorage.setItem('assignedSensorSort', JSON.stringify(assignedSort));
  }, [assignedSort]);

  // 列設定変更時にlocalStorageに保存（DataTableコンポーネント内で処理）

  // 列幅リサイズはDataTableコンポーネント内で処理

  // 日時フォーマット関数
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('ja-JP', { 
      year: 'numeric',
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ONLINE/OFFLINE判定関数（最終応答日時が10分以内ならONLINE）
  const isOnline = (lastResponseAt) => {
    if (!lastResponseAt) return false;
    const now = new Date();
    const lastResponse = new Date(lastResponseAt);
    const diffMinutes = (now - lastResponse) / 1000 / 60;
    return diffMinutes <= 10;
  };

  // MACアドレスから下4桁を取得（例: 58:8C:81:9F:D2:DC → d2dc）
  const getMacLast4Digits = (macAddress) => {
    return macAddress.replace(/:/g, '').slice(-4).toLowerCase();
  };

  // mDNS名でデバイス設定画面を開く（3回リトライ）
  const handleOpenDeviceSettings = async (macAddress) => {
    const last4 = getMacLast4Digits(macAddress);
    const mdnsUrl = `http://cleanbelt-${last4}.local`;
    const maxRetries = 3;
    
    // モーダルを表示
    setShowConnectionModal(true);
    setConnectionAttempt(0);
    setConnectionStatus('接続を確認しています...');
    
    // 接続確認用の画像読み込み（タイムアウト5秒）
    const checkConnection = () => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const timeout = setTimeout(() => {
          img.src = ''; // 読み込み中止
          reject(new Error('Timeout'));
        }, 5000);
        
        img.onload = () => {
          clearTimeout(timeout);
          resolve(true);
        };
        
        img.onerror = () => {
          clearTimeout(timeout);
          // 404エラーでも接続は成功（デバイスが応答している）
          resolve(true);
        };
        
        // faviconまたはルートパスを試す
        img.src = `${mdnsUrl}/favicon.ico?_=${Date.now()}`;
      });
    };
    
    // リトライロジック
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      setConnectionAttempt(attempt);
      setConnectionStatus(`接続を確認しています... (${attempt}/${maxRetries})`);
      
      try {
        await checkConnection();
        // 接続成功
        setConnectionStatus('接続成功！デバイス設定画面を開きます...');
        await new Promise(resolve => setTimeout(resolve, 500)); // 成功メッセージを表示
        setShowConnectionModal(false);
        window.open(mdnsUrl, '_blank');
        return;
      } catch (error) {
        if (attempt < maxRetries) {
          setConnectionStatus(`接続失敗。再試行中... (${attempt}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, 1000)); // 1秒待ってリトライ
        }
      }
    }
    
    // 全てのリトライが失敗
    setConnectionStatus('接続に失敗しました');
    await new Promise(resolve => setTimeout(resolve, 1000));
    setShowConnectionModal(false);
    alert(`デバイスに接続できませんでした。\n\nデバイスがオフラインか、同じネットワークに接続していない可能性があります。\nmDNS名: ${mdnsUrl}`);
  };

  // センサーテーブル用カスタムセルレンダラー
  const renderSensorCell = (sensor, column, cellStyle) => {
    switch (column.id) {
      case 'mac_address':
        const macOnline = isOnline(sensor.last_response_at);
        return (
          <td key={column.id} style={cellStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <code>{sensor.mac_address}</code>
              {macOnline && (
                <span
                  onClick={() => handleOpenDeviceSettings(sensor.mac_address)}
                  style={{
                    cursor: 'pointer',
                    fontSize: '18px',
                    color: '#10b981',
                    transition: 'transform 0.2s',
                    display: 'inline-block'
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'rotate(90deg)'}
                  onMouseLeave={(e) => e.target.style.transform = 'rotate(0deg)'}
                  title="デバイス設定画面を開く（mDNS経由）"
                >
                  ⚙️
                </span>
              )}
            </div>
          </td>
        );
      case 'sensor_name':
        return <td key={column.id} style={cellStyle}>{sensor.sensor_name || '-'}</td>;
      case 'assigned_washer':
        // 割り当て済みセンサーの場合
        if (sensor.current_washer_name) {
          return (
            <td key={column.id} style={cellStyle}>
              <div>
                <strong>{sensor.current_washer_name}</strong>
                {sensor.current_washer_code && (
                  <><br /><small><code>{sensor.current_washer_code}</code></small></>
                )}
              </div>
              <button
                className="btn-small btn-warning"
                onClick={() => handleUnassign(sensor)}
                style={{ marginTop: '8px' }}
              >
                解除
              </button>
            </td>
          );
        }
        // 未割り当てセンサーの場合
        return (
          <td key={column.id} style={cellStyle}>
            <button
              className="btn-small btn-primary"
              onClick={() => handleAssignClick(sensor)}
            >
              割り当て
            </button>
          </td>
        );
      case 'status':
        const online = isOnline(sensor.last_response_at);
        return (
          <td key={column.id} style={cellStyle}>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <span className={`badge badge-${sensor.status === 'active' ? 'success' : 'default'}`}>
                {sensor.status === 'active' ? '稼働中' : sensor.status === 'inactive' ? '停止中' : 'メンテナンス中'}
              </span>
              <span className={`badge badge-${online ? 'online' : 'offline'}`}>
                {online ? 'ONLINE' : 'OFFLINE'}
              </span>
            </div>
          </td>
        );
      case 'notes':
        return <td key={column.id} style={cellStyle}>{sensor.notes || '-'}</td>;
      case 'last_boot_at':
        return <td key={column.id} style={{ ...cellStyle, fontSize: '13px' }}>{formatDateTime(sensor.last_boot_at)}</td>;
      case 'last_response_at':
        return <td key={column.id} style={{ ...cellStyle, fontSize: '13px' }}>{formatDateTime(sensor.last_response_at)}</td>;
      case 'actions':
        return (
          <td key={column.id} style={cellStyle}>
            <div className="action-buttons">
              {sensor.washer_id && (
                <button
                  className="btn-small btn-primary"
                  onClick={() => handleAssignClick(sensor)}
                >
                  交換
                </button>
              )}
              <button
                className="btn-small btn-secondary"
                onClick={() => handleEditClick(sensor)}
              >
                編集
              </button>
              <button
                className="btn-small"
                onClick={() => handleViewLogs(sensor.mac_address)}
                style={{ background: '#805ad5', color: 'white' }}
                title="センサーイベントログを表示"
              >
                ログ
              </button>
              {!sensor.washer_id && (
                <button
                  className="btn-small btn-danger"
                  onClick={() => handleDelete(sensor)}
                >
                  削除
                </button>
              )}
            </div>
          </td>
        );
      default:
        return undefined; // デフォルトレンダリング
    }
  };

  // ログ画面への遷移
  const handleViewLogs = (macAddress) => {
    if (onNavigateToLogs) {
      onNavigateToLogs(macAddress);
    }
  };

  // センサー一覧取得
  const loadSensors = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/sensors`);
      setSensors(response.data);
    } catch (error) {
      console.error('Failed to load sensors:', error);
      alert('センサー一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // センサー一覧の静かな更新（自動更新用・ローディング表示なし）
  const refreshSensors = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/sensors`);
      setSensors(response.data);
    } catch (error) {
      console.error('Failed to refresh sensors:', error);
      // 自動更新時はエラーダイアログを表示しない
    }
  };

  // 自動更新（表示中のタブのみ）
  useEffect(() => {
    if (!autoRefreshEnabled) {
      return;
    }
    
    let interval = null;
    
    const startAutoRefresh = () => {
      if (interval) clearInterval(interval);
      
      if (document.visibilityState === 'visible') {
        interval = setInterval(() => {
          refreshSensors();
        }, 2000);
      }
    };
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshSensors();
        startAutoRefresh();
      } else {
        if (interval) {
          clearInterval(interval);
          interval = null;
        }
      }
    };
    
    startAutoRefresh();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      if (interval) clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [autoRefreshEnabled]);

  // 会社一覧取得
  const loadCompanies = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/companies`);
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  // 洗浄機一覧取得（全社の洗浄機を取得）
  const loadWashers = async () => {
    try {
      // 全社の洗浄機を取得するため、まず会社一覧を取得
      const companiesResponse = await axios.get(`${API_BASE_URL}/companies`);
      const allWashers = [];
      
      for (const company of companiesResponse.data) {
        const washersResponse = await axios.get(`${API_BASE_URL}/companies/${company.company_id}/washers`);
        allWashers.push(...washersResponse.data);
      }
      
      setWashers(allWashers);
    } catch (error) {
      console.error('Failed to load washers:', error);
    }
  };

  useEffect(() => {
    loadSensors();
    loadWashers();
    loadCompanies();
  }, []);

  // 新規追加ボタン
  const handleAdd = () => {
    setEditingSensor(null);
    setSensorForm({
      mac_address: '',
      sensor_name: '',
      status: 'active',
      notes: ''
    });
    setShowForm(true);
  };

  // 編集ボタン
  const handleEditClick = (sensor) => {
    setEditingSensor(sensor);
    setSensorForm({
      mac_address: sensor.mac_address,
      sensor_name: sensor.sensor_name || '',
      status: sensor.status,
      notes: sensor.notes || ''
    });
    setShowForm(true);
  };

  // 保存
  const handleSave = async () => {
    try {
      if (editingSensor) {
        // 更新
        await axios.put(`${API_BASE_URL}/sensors/${editingSensor.sensor_id}`, sensorForm);
        alert('センサー情報を更新しました');
      } else {
        // 新規登録
        await axios.post(`${API_BASE_URL}/sensors`, sensorForm);
        alert('センサーを登録しました');
      }
      setShowForm(false);
      loadSensors();
    } catch (error) {
      console.error('Failed to save sensor:', error);
      alert(`センサーの保存に失敗しました: ${error.response?.data?.detail || error.message}`);
    }
  };

  // 削除
  const handleDelete = async (sensor) => {
    if (!window.confirm(`センサー「${sensor.mac_address}」を削除してもよろしいですか？\n\n割り当て中のセンサーは削除できません。`)) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/sensors/${sensor.sensor_id}`);
      alert('センサーを削除しました');
      loadSensors();
    } catch (error) {
      console.error('Failed to delete sensor:', error);
      alert(`センサーの削除に失敗しました: ${error.response?.data?.detail || error.message}`);
    }
  };

  // 洗浄機に割り当て
  const handleAssignClick = (sensor) => {
    setSelectedSensor(sensor);
    setShowAssignModal(true);
  };

  // 割り当て実行
  const handleAssignToWasher = async (washer) => {
    if (!selectedSensor) return;

    try {
      await axios.post(`${API_BASE_URL}/sensors/${selectedSensor.sensor_id}/assignments`, {
        washer_id: washer.washer_id
      });
      alert(`センサー「${selectedSensor.mac_address}」を洗浄機「${washer.washer_name}」に割り当てました`);
      
      setShowAssignModal(false);
      setSelectedSensor(null);
      loadSensors();
      loadWashers();
    } catch (error) {
      console.error('Failed to assign sensor:', error);
      alert(`センサーの割り当てに失敗しました: ${error.response?.data?.detail || error.message}`);
    }
  };

  // 割り当て解除
  const handleUnassign = async (sensor) => {
    if (!sensor.current_assignment_id) {
      alert('割り当てられていません');
      return;
    }

    if (!window.confirm(`センサー「${sensor.mac_address}」の割り当てを解除してもよろしいですか？`)) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/sensor_assignments/${sensor.current_assignment_id}`);
      alert('センサーの割り当てを解除しました');
      loadSensors();
      loadWashers();
    } catch (error) {
      console.error('Failed to unassign sensor:', error);
      alert(`割り当て解除に失敗しました: ${error.response?.data?.detail || error.message}`);
    }
  };

  // 未割り当てセンサーと割り当て済みセンサーで分類
  const unassignedSensors = sensors.filter(s => !s.current_washer_id);
  
  // すべての割り当て済みセンサー
  const allAssignedSensors = sensors.filter(s => s.current_washer_id);
  
  // 割り当て済みセンサー（会社でフィルタリング）
  const assignedSensors = sensors.filter(s => {
    if (!s.current_washer_id) return false;
    
    // 会社が選択されている場合は会社でフィルタ
    if (selectedCompany) {
      const washer = washers.find(w => w.washer_id === s.current_washer_id);
      return washer && washer.company_id === selectedCompany.company_id;
    }
    
    return false; // 会社未選択の場合は非表示
  });
  
  // 未割り当てセンサーのソートとページング
  const getUnassignedPageData = () => {
    let sorted = [...unassignedSensors];
    if (unassignedSort && unassignedSort.column) {
      sorted.sort((a, b) => {
        let aVal = a[unassignedSort.column];
        let bVal = b[unassignedSort.column];
        
        // assigned_washer列は実際にはcurrent_washer_nameでソート
        if (unassignedSort.column === 'assigned_washer') {
          aVal = a.current_washer_name || '';
          bVal = b.current_washer_name || '';
        }
        
        if (aVal === null || aVal === undefined || aVal === '') return 1;
        if (bVal === null || bVal === undefined || bVal === '') return -1;
        
        let comparison = 0;
        // 日時列の場合はDateとして比較
        if (unassignedSort.column === 'last_boot_at' || unassignedSort.column === 'last_response_at') {
          const aDate = new Date(aVal);
          const bDate = new Date(bVal);
          comparison = aDate - bDate;
        } else if (typeof aVal === 'string') {
          comparison = aVal.localeCompare(bVal, 'ja');
        } else {
          comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        }
        
        return unassignedSort.order === 'asc' ? comparison : -comparison;
      });
    }
    
    const startIndex = (unassignedPage - 1) * unassignedPageSize;
    const endIndex = startIndex + unassignedPageSize;
    return sorted.slice(startIndex, endIndex);
  };
  
  // 割り当て済みセンサーのソートとページング
  const getAssignedPageData = () => {
    let sorted = [...assignedSensors];
    if (assignedSort && assignedSort.column) {
      sorted.sort((a, b) => {
        let aVal = a[assignedSort.column];
        let bVal = b[assignedSort.column];
        
        // assigned_washer列は実際にはcurrent_washer_nameでソート
        if (assignedSort.column === 'assigned_washer') {
          aVal = a.current_washer_name || '';
          bVal = b.current_washer_name || '';
        }
        
        if (aVal === null || aVal === undefined || aVal === '') return 1;
        if (bVal === null || bVal === undefined || bVal === '') return -1;
        
        let comparison = 0;
        // 日時列の場合はDateとして比較
        if (assignedSort.column === 'last_boot_at' || assignedSort.column === 'last_response_at') {
          const aDate = new Date(aVal);
          const bDate = new Date(bVal);
          comparison = aDate - bDate;
        } else if (typeof aVal === 'string') {
          comparison = aVal.localeCompare(bVal, 'ja');
        } else {
          comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        }
        
        return assignedSort.order === 'asc' ? comparison : -comparison;
      });
    }
    
    const startIndex = (assignedPage - 1) * assignedPageSize;
    const endIndex = startIndex + assignedPageSize;
    return sorted.slice(startIndex, endIndex);
  };

  // センサー未割り当ての洗浄機のみ表示（会社でもフィルタ）
  const availableWashers = washers.filter(w => {
    // センサー未割り当てかチェック
    const isUnassigned = !sensors.some(s => s.current_washer_id === w.washer_id);
    
    // 会社が選択されている場合は会社でもフィルタ
    const matchesCompany = !selectedCompany || w.company_id === selectedCompany.company_id;
    
    return isUnassigned && matchesCompany;
  });

  return (
    <div className={onClose ? "modal-overlay" : ""} onClick={onClose ? onClose : undefined}>
      <div className={onClose ? "modal-content sensor-management-modal" : "sensor-management-content"} onClick={(e) => onClose && e.stopPropagation()}>
        <div className="modal-header">
          <h2>センサー管理</h2>
          {onClose && <button className="modal-close" onClick={onClose}>×</button>}
        </div>

        {loading ? (
          <div className="loading-text">読み込み中...</div>
        ) : (
          <>
            {/* 未割り当てセンサー */}
            <div className="sensor-section">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
                <h3 className="section-title unassigned" style={{ marginBottom: 0 }}>未割り当てセンサー ({unassignedSensors.length}件)</h3>
                <button className="btn btn-primary" onClick={handleAdd}>
                  + 新規登録
                </button>
              </div>

              {unassignedSensors.length > 0 ? (
                <DataTable
                  columns={sensorColumns}
                  data={getUnassignedPageData()}
                  defaultColumns={defaultSensorColumns}
                  onColumnsChange={setSensorColumns}
                  localStorageKey="sensorColumns"
                  sortConfig={unassignedSort}
                  onSortChange={setUnassignedSort}
                  currentPage={unassignedPage}
                  pageSize={unassignedPageSize}
                  totalItems={unassignedSensors.length}
                  onPageChange={setUnassignedPage}
                  onPageSizeChange={setUnassignedPageSize}
                  idField="sensor_id"
                  renderCell={renderSensorCell}
                  emptyMessage="未割り当てセンサーがありません"
                />
              ) : (
                <div className="empty-message" style={{ padding: '20px', textAlign: 'center' }}>
                  未割り当てセンサーがありません
                </div>
              )}
            </div>

            {/* 割り当て済みセンサー */}
            {allAssignedSensors.length > 0 && (
              <div className="sensor-section">
                <h3 className="section-title assigned">割り当て済みセンサー ({allAssignedSensors.length}件)</h3>
                
                {!selectedCompany ? (
                  <p className="empty-message">グローバルメニューから会社を選択してください。</p>
                ) : assignedSensors.length === 0 ? (
                  <p className="empty-message">この会社に割り当て済みセンサーがありません。</p>
                ) : (
                  <DataTable
                    columns={sensorColumns}
                    data={getAssignedPageData()}
                    defaultColumns={defaultSensorColumns}
                    onColumnsChange={setSensorColumns}
                    localStorageKey="sensorColumns"
                    sortConfig={assignedSort}
                    onSortChange={setAssignedSort}
                    currentPage={assignedPage}
                    pageSize={assignedPageSize}
                    totalItems={assignedSensors.length}
                    onPageChange={setAssignedPage}
                    onPageSizeChange={setAssignedPageSize}
                    idField="sensor_id"
                    renderCell={renderSensorCell}
                    emptyMessage="割り当て済みセンサーがありません"
                  />
                )}
              </div>
            )}
          </>
        )}

        {/* センサー登録・編集モーダル */}
        {showForm && (
          <Modal
            title={editingSensor ? 'センサー編集' : 'センサー新規登録'}
            onClose={() => setShowForm(false)}
            width="500px"
          >
            <div className="form-group">
              <label>MACアドレス *</label>
              <input
                type="text"
                value={sensorForm.mac_address}
                onChange={(e) => setSensorForm({ ...sensorForm, mac_address: e.target.value })}
                placeholder="例: AA:BB:CC:DD:EE:FF"
                pattern="^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$"
              />
              <small>形式: XX:XX:XX:XX:XX:XX または XX-XX-XX-XX-XX-XX</small>
            </div>

            <div className="form-group">
              <label>センサー名</label>
              <input
                type="text"
                value={sensorForm.sensor_name}
                onChange={(e) => setSensorForm({ ...sensorForm, sensor_name: e.target.value })}
                placeholder="例: センサー1号機"
              />
            </div>

            <div className="form-group">
              <label>ステータス</label>
              <select
                value={sensorForm.status}
                onChange={(e) => setSensorForm({ ...sensorForm, status: e.target.value })}
              >
                <option value="active">稼働中</option>
                <option value="inactive">停止中</option>
                <option value="maintenance">メンテナンス中</option>
              </select>
            </div>

            <div className="form-group">
              <label>備考</label>
              <textarea
                value={sensorForm.notes}
                onChange={(e) => setSensorForm({ ...sensorForm, notes: e.target.value })}
                placeholder="センサーに関する備考を入力"
                rows="3"
              />
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowForm(false)}>
                キャンセル
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={!sensorForm.mac_address}
              >
                {editingSensor ? '更新' : '登録'}
              </button>
            </div>
          </Modal>
        )}

        {/* デバイス接続モーダル */}
        {showConnectionModal && (
          <div className="connection-modal-overlay" onClick={() => {}}>
            <div className="connection-modal" onClick={(e) => e.stopPropagation()}>
              <div className="connection-spinner"></div>
              <div className="connection-status">
                {connectionStatus}
              </div>
              {connectionAttempt > 0 && (
                <div className="connection-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${(connectionAttempt / 3) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 洗浄機選択モーダル */}
        {showAssignModal && (
          <Modal
            title="洗浄機を選択"
            onClose={() => setShowAssignModal(false)}
            width="800px"
          >
            <p className="info-text">
              センサー <strong>{selectedSensor?.mac_address}</strong> を以下の洗浄機に割り当てます。
            </p>

            {!selectedCompany ? (
              <p className="empty-message">グローバルメニューから会社を選択してください。</p>
            ) : availableWashers.length === 0 ? (
              <p className="empty-message">センサー未割り当ての洗浄機がありません。</p>
            ) : (
              <table className="washer-select-table">
                <thead>
                  <tr>
                    <th>洗浄機コード</th>
                    <th>洗浄機名</th>
                    <th>シリアル番号</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {availableWashers.map(washer => (
                    <tr key={washer.washer_id}>
                      <td><code>{washer.washer_code || '-'}</code></td>
                      <td><strong>{washer.washer_name}</strong></td>
                      <td>{washer.serial_number || '-'}</td>
                      <td>
                        <button
                          className="btn-small btn-primary"
                          onClick={() => handleAssignToWasher(washer)}
                        >
                          この洗浄機に割り当て
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Modal>
        )}
      </div>
    </div>
  );
};

export default SensorManagement;
